<template>
  <ExpansionCard
    :columnCollapsed="columnCollapsed"
    :overflow="false"
    hideDropdownOnMobile
    @expanded="scrollToActiveParagraph()"
  >
    <template #title>
      <div class="truncate px-1 py-0.5 text-6.5pt font-bold">
        {{ legalText.name }}
      </div>
    </template>
    <template #subtitle>
      <div class="flex w-full justify-center gap-2 border-b px-0.5 pb-2 pt-1">
        <ParagraphLink
          v-tooltip.bottom="t('toToc')"
          class="flex size-7 rounded p-1 font-semibold capitalize text-black transition hover:bg-gray-300 hover:text-black mobile-landscape:hidden"
          :legalText="legalText.slug"
          :title="t('toToc')"
        >
          <i-ri-list-unordered
            class="h-full w-full rotate-180 rounded-[2px] border border-gray-300 p-px"
          />
        </ParagraphLink>
        <button
          v-if="!columnCollapsed"
          v-tooltip.bottom="t('collapse')"
          class="ml-auto size-7 rounded p-1.5 transition hover:bg-gray-300"
          :class="{ 'bg-gray-200': fullyCollapsed }"
          @click="closeList"
        >
          <span class="sr-only">{{ t("collapse") }}</span>
          <i-ri-menu-fold-2-line class="h-full w-full" />
        </button>
        <button
          v-if="!columnCollapsed"
          v-tooltip.bottom="t('expand')"
          class="size-7 rounded p-1.5 transition hover:bg-gray-300"
          :class="{ 'bg-gray-200': fullyExpanded }"
          @click="openList"
        >
          <span class="sr-only">{{ t("expand") }}</span>
          <i-ri-menu-unfold-2-line class="h-full w-full" />
        </button>
      </div>
    </template>
    <template #default>
      <div class="flex h-full flex-col">
        <!-- setting height is a must for use by useVirtualList do display limited items -->
        <div
          v-bind="containerProps"
          class="h-full min-h-0 overflow-x-hidden px-1"
          :class="{ 'scrollbar-hidden': columnCollapsed }"
        >
          <div v-if="loading" class="flex h-96 items-center justify-center">
            <i-line-md-loading-twotone-loop
              class="h-10 w-10"
            ></i-line-md-loading-twotone-loop>
          </div>
          <ul v-else v-bind="wrapperProps" class="list-none" tabindex="0">
            <li
              v-for="{ data } in list"
              :key="`${isParagraph(data) ? 'p' : 't'}-${data.id}`"
              class="py-0.5"
              :class="marginLeft(data)"
            >
              <TocParagraph
                v-if="isParagraph(data)"
                tabindex="-1"
                :columnCollapsed="columnCollapsed"
                :paragraph="data"
                :active="legalTextParagraph?.id === data.id"
                :legalTextSlug="legalText.slug"
                :bookmark="
                  bookmarks.find((b) => b.legal_text_paragraph_id === data.id)
                "
              />
              <TocButton
                v-else
                tabindex="-1"
                :legalTextToc="data"
                :parentOfActive="isParentOfActiveParagraph(data) !== undefined"
                :open="tocOpen(data)"
                :columnCollapsed="columnCollapsed"
                @updateList="handleTocClick(data)"
              />
            </li>
          </ul>
        </div>
      </div>
    </template>
  </ExpansionCard>
</template>

<script lang="ts" setup>
import type { IToC } from "~/models/IToC";
import type {
  IBookmark,
  ILegalTextParagraph,
} from "~/models/ILegalTextParagraph";
import type { ILegalText } from "~/models/ILegalText";

type Props = {
  legalTextParagraph?: ILegalTextParagraph;
  nestedToc?: IToC[];
  legalText: ILegalText;
  flatToc?: (IToC | ILegalTextParagraph)[];
  parentsOfActiveParagraph: IToC[];
  columnCollapsed: boolean;
  bookmarks: IBookmark[];
  loading: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  legalTextParagraph: undefined,
  flatToc: () => [],
  nestedToc: () => [],
});

const {
  nestedToc,
  flatToc,
  legalTextParagraph,
  parentsOfActiveParagraph,
  loading,
} = toRefs(props);

const { t } = useI18n({ useScope: "local" });

const {
  handleTocClick,
  closeList,
  openList,
  tocOpen,
  isParagraph,
  isParentOfActiveParagraph,
  scrollToActiveParagraph,
  wrapperProps,
  containerProps,
  list,
  fullyCollapsed,
  fullyExpanded,
} = useVirtualizedToC({
  flatToc,
  nestedToc,
  parentsOfActiveParagraph,
  legalTextParagraph,
  itemHeight: 24, // increasing will add white space at the bottom while decreasing could affect short articles e.g gesetz/artikel-115-gesetz
});

const marginLeft = (toc: ILegalTextParagraph | IToC) => {
  if (props.columnCollapsed) {
    return "ml-0";
  }

  if (!isParagraph(toc)) {
    return `ml-${toc.depth}`;
  }

  const index = flatToc.value.indexOf(toc);
  const prevList = flatToc.value.slice(0, index);
  const prevToc = prevList.reverse().find((item) => !isParagraph(item)) as IToC;

  if (prevToc) {
    return `ml-${prevToc.depth}`;
  }

  return "";
};
</script>

<i18n>
{
  "de": {
    "toToc": "Zum @:legal_text.toc",
    "collapse": "Alle Überschriften einklappen",
    "expand": "Alle Überschriften ausklappen"
  },
  "en": {
    "toToc": "To @:legal_text.toc",
    "collapse": "Collapse all headings",
    "expand": "Expand all headings"
  }
}
</i18n>
